import { action, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { TreeStore } from '@/stores/local/shared/TreeStore';
import call from '@/utils/api/api';
import { downloadFile } from '@/utils/downloadFile';
import { fetchDocumentDownloadLink } from '@/utils/fetchDocumentDownloadLink';

type DocumentActionsStoreParams = {
  documentId?: string | null;
  organizationId?: string;
  load?: () => void;
  treeStore?: TreeStore;
};

export class DocumentActionsStore implements ILocalStore {
  private _documentId?: string | null;
  private readonly _organizationId?: string;
  private _load?: () => void;
  private readonly _treeStore?: TreeStore;

  readonly deleteMeta = new MetaStore();
  readonly downloadMeta = new MetaStore();

  constructor({ documentId, organizationId, load, treeStore }: DocumentActionsStoreParams) {
    this._documentId = documentId;
    this._organizationId = organizationId;
    this._load = load;
    this._treeStore = treeStore;

    makeObservable<DocumentActionsStore, '_documentId'>(this, {
      _documentId: observable,
      setDocumentId: action.bound,
    });
  }

  deleteDocument = async () => {
    if (!this._documentId || !this._organizationId) {
      notificationsStore.showError('Не удалось удалить документ');

      return;
    }

    this.deleteMeta.setLoadedStartMeta();

    const response = await call({
      endpoint: ENDPOINTS.documents.delete(this._organizationId, this._documentId),
      method: 'DELETE',
    });

    if (response.isError) {
      notificationsStore.showError(response.error.response?.data.message ?? 'Не удалось удалить документ');
      this.deleteMeta.setLoadedErrorMeta();

      return;
    }

    runInAction(() => {
      if (this._load) {
        this._load();
      }

      if (this._treeStore && this._documentId) {
        this._treeStore.removeNode(this._documentId);
      }

      this.deleteMeta.setLoadedSuccessMeta();
      notificationsStore.showSuccess('Документ удален');
    });
  };

  setDocumentId(id?: string | null) {
    this._documentId = id;
  }

  downloadDocument = async (): Promise<void> => {
    if (!this._documentId) {
      return;
    }

    this.downloadMeta.setLoadedStartMeta();

    const result = await fetchDocumentDownloadLink({
      documentId: this._documentId,
      organizationId: this._organizationId,
    });

    if (!result.success) {
      notificationsStore.showError(result.errorMessage ?? 'Не удалось скачать файл');
      this.downloadMeta.setLoadedErrorMeta();

      return;
    }

    runInAction(() => {
      downloadFile(result.link);
      this.downloadMeta.setLoadedSuccessMeta();
    });
  };

  destroy() {
    this.deleteMeta.destroy();
    this.downloadMeta.destroy();
  }
}
